import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IChannelBudgets, IRecommendedSplit, OmniCampaign } from '@feathr/blackbox';
import {
  Alert,
  CardV2 as Card,
  SegmentedControl,
  Skeleton,
  SplitSlider,
  Text,
  Tooltip,
} from '@feathr/components';
import { getIconForAction, useDeferredLoading } from '@feathr/hooks';
import type { IUseAudienceBreakdown } from '@feathr/report_components';

import { getDays } from '../AdWizardBudgetStep.utils';
import { useChannelBudgetSplit } from './ChannelBudgetSplit.hooks';
import {
  formatMetaSpendFloor,
  isMetaBelowSpendFloor,
  isSplitBelowFloor,
  MAX_SPLIT,
  MIN_SPLIT,
} from './ChannelBudgetSplit.utils';

import * as styles from './ChannelBudgetSplit.css';

/**
 * Props for the `ChannelBudgetSplit` card that lets users divide an OmniCampaign's
 * total budget between the Display (TTD) and Meta channels.
 */
interface IChannelBudgetSplitProps {
  /** OmniCampaign whose `exposure_settings.channel_split` is being edited. */
  campaign: OmniCampaign;
  /** Audience breakdown used to derive the recommended split and budgets. */
  audienceData: IUseAudienceBreakdown;
  /** Backend per-channel minimum budgets; falls back to the $5/day floor when null. */
  channelMinimums?: IChannelBudgets | null;
  /** Lowest Meta budget Meta will accept; null when there is no Meta spend. */
  metaSpendFloor?: number | null;
  /** Backend-recommended split; falls back to 60/40 when null. */
  recommendedSplit?: IRecommendedSplit | null;
  /** Backend-recommended per-channel dollar amounts; used as the shown dollars in recommended mode. */
  recommendedAmounts?: { display: number; meta: number } | null;
  /**
   * Whether a fresh recommendation is being fetched (e.g. the total budget
   * changed). The per-channel percentages and dollar amounts recompute from it,
   * so they show a loader while it's in flight.
   */
  isRecommendationLoading?: boolean;
  /** Hides the recommended/manual toggle where the split is accepted rather than edited. */
  hideModeToggle?: boolean;
}

function ChannelBudgetSplit({
  campaign,
  audienceData,
  channelMinimums,
  metaSpendFloor,
  recommendedSplit,
  recommendedAmounts,
  isRecommendationLoading = false,
  hideModeToggle = false,
}: Readonly<IChannelBudgetSplitProps>): JSX.Element {
  const { t } = useTranslation();

  const {
    displayPercent,
    handleSliderChange,
    displayBudget,
    metaBudget,
    percentDisplayBudget,
    percentMetaBudget,
    showAudienceDriftWarning,
    handleSliderChangeEnd,
    handleUseRecommended,
    handleSetManual,
    mode,
    recommendedDisplayPercent,
    isSplitLocked,
  } = useChannelBudgetSplit({ campaign, channelMinimums, recommendedSplit, recommendedAmounts });

  const metaPercent = 100 - displayPercent;
  const recommendationDiff = Math.abs(displayPercent - recommendedDisplayPercent);
  const isMatchingRecommended = recommendationDiff === 0;
  const recommendationText = isMatchingRecommended
    ? t("You're matching the recommended split")
    : t('Off recommendation by {{diff}}%', { diff: recommendationDiff });

  // Inline preview of the same check `validateStepBudget` uses to block save.
  // Only meaningful once the backend per-channel minimums have loaded.
  const isBelowFloor = isSplitBelowFloor({ displayBudget, metaBudget, channelMinimums });
  // Same Meta dollars as validateStepBudget; recommended mode may display different amounts.
  const isBelowSpendFloor = isMetaBelowSpendFloor({
    metaBudget: percentMetaBudget,
    metaSpendFloor,
  });

  // Flag the specific channel that's short so its amount can be marked invalid.
  const displayBelowMin =
    isBelowFloor && !!channelMinimums && displayBudget < channelMinimums.display;
  const metaBelowMin =
    (isBelowFloor && !!channelMinimums && metaBudget < channelMinimums.meta) || isBelowSpendFloor;

  /*
   * Under the spend floor, show the dollars the split saves. The recommended-mode amounts can
   * clear the floor even when the saved percentage does not, which would show a passing amount
   * in red under an alert saying it fails.
   */
  const shownDisplayBudget = isBelowSpendFloor ? percentDisplayBudget : displayBudget;
  const shownMetaBudget = isBelowSpendFloor ? percentMetaBudget : metaBudget;

  const days = getDays(campaign);
  const spendFloorFormatted =
    metaSpendFloor !== null && metaSpendFloor !== undefined
      ? formatMetaSpendFloor(metaSpendFloor)
      : null;
  const floorCopyValues = channelMinimums
    ? {
        days,
        display: numeral(channelMinimums.display).format('$0,0'),
        meta: numeral(channelMinimums.meta).format('$0,0'),
      }
    : null;

  /* `useAudienceBreakdown` initializes `isLoading=false` and only flips
   * it true inside its first effect, which causes a brief flash of content
   * before the skeleton. `useDeferredLoading` forces the first render to
   * be loading and follows the upstream flag from there. */
  const isLoading = useDeferredLoading(audienceData.isLoading);

  /*
   * Disable the mode tabs and slider when the split is locked at the minimum, or
   * while a fresh recommendation is loading — the values are about to change, so
   * adjusting them would fight the incoming split.
   */
  const areControlsDisabled = isSplitLocked || isRecommendationLoading;

  const tooltipContent = t(
    'Adjust how your budget is divided between Display and Meta advertising channels. The default split is based on the relative audience sizes for each channel.',
  );

  const titleSuffix = (
    <Tooltip title={tooltipContent}>
      <FontAwesomeIcon icon={faCircleInfo} />
    </Tooltip>
  );

  // Where the toggle is hidden the budget is read-only, so "raise it" is not actionable.
  const canAdjustBudget = !hideModeToggle;

  let description: string;
  if (isSplitLocked) {
    description = canAdjustBudget
      ? t(
          'At the minimum budget the split is fixed at each channel’s minimum. Raise the total budget to adjust it.',
        )
      : t('At the minimum budget the split is fixed at each channel’s minimum.');
  } else if (floorCopyValues) {
    description = t(
      'Drag the divider to adjust the split between Display and Meta. For a {{days}}-day campaign, Display needs at least {{display}} and Meta at least {{meta}}.',
      floorCopyValues,
    );
  } else {
    description = t('Drag the divider to adjust the split between Display and Meta.');
  }

  // Shown on the disabled controls to explain why the split can't be adjusted.
  const lockTooltip = canAdjustBudget
    ? t(
        'At the minimum budget, each channel needs its exact minimum, so the split is fixed. Raise the total budget to adjust it.',
      )
    : t('At the minimum budget, each channel needs its exact minimum, so the split is fixed.');

  const modeOptions = [
    { value: 'recommended', label: t('Use recommended split') },
    { value: 'manual', label: t('Set manually') },
  ];

  function handleModeChange(next: string): void {
    if (next === 'recommended') {
      handleUseRecommended();
    } else {
      handleSetManual();
    }
  }

  // The spend-floor alert wins over the per-channel-minimum alert; otherwise show the recommendation text.
  function renderSplitStatus(): JSX.Element {
    if (isBelowSpendFloor && spendFloorFormatted) {
      return (
        <Alert
          className={styles.flushAlert}
          description={t(
            "Meta doesn't allow lowering the budget below {{floor}} once a campaign has spent. Drag the slider or raise the total budget to fix it.",
            { floor: spendFloorFormatted },
          )}
          name={'channel_split_below_spend_floor'}
          title={t('This split is below what Meta will accept')}
          type={'danger'}
        />
      );
    }
    if (isBelowFloor && floorCopyValues) {
      return (
        <Alert
          className={styles.flushAlert}
          description={t(
            "For a {{days}}-day campaign, Display needs at least {{display}} and Meta at least {{meta}}. Drag the slider or adjust the campaign's total budget to fix it.",
            floorCopyValues,
          )}
          name={'channel_split_below_floor'}
          title={t('This split is below the per-channel minimum')}
          type={'danger'}
        />
      );
    }
    return (
      <Text.SmallRegular className={styles.recommendationText}>
        {recommendationText}
      </Text.SmallRegular>
    );
  }

  return (
    <Card width={'narrow'}>
      <Card.Header
        // Derived from the minimums, so it rewrites itself as they land.
        description={
          isLoading || isRecommendationLoading ? (
            <Skeleton height={16} width={'90%'} />
          ) : (
            description
          )
        }
        title={t('Channel Budget Split')}
        titleSuffix={titleSuffix}
      />
      <Card.Content isLoading={isLoading}>
        <Flex className={styles.cardSection} direction={'column'}>
          {showAudienceDriftWarning && (
            <Alert
              description={t(
                'Your audience selection has changed. Please review your budget split to ensure it still aligns with your campaign goals.',
              )}
              name={'audience_drift_warning'}
              title={t('Audience has changed')}
              type={'warning'}
            />
          )}

          {!hideModeToggle && (
            <SegmentedControl
              data={modeOptions}
              disabled={areControlsDisabled}
              name={'budget_split_mode'}
              onChange={handleModeChange}
              value={mode}
            />
          )}

          <Flex className={styles.summaryRow} justify={'space-between'}>
            <Flex className={styles.summaryColumn} direction={'column'}>
              <Text.DefaultSemibold>{t('Display')}</Text.DefaultSemibold>
              <Flex direction={'column'}>
                {isRecommendationLoading ? (
                  <>
                    <Skeleton height={16} width={32} />
                    <Skeleton height={24} width={80} />
                  </>
                ) : (
                  <>
                    <Text.SmallRegular>
                      {numeral(displayPercent / 100).format('0%')}
                    </Text.SmallRegular>
                    <Text.LargeSemibold
                      className={classNames(styles.dollarAmount, {
                        [styles.dollarAmountInvalid]: displayBelowMin,
                      })}
                    >
                      {numeral(shownDisplayBudget).format('$0,0.00')}
                    </Text.LargeSemibold>
                  </>
                )}
              </Flex>
            </Flex>
            <Flex className={styles.summaryColumnRight} direction={'column'}>
              <Text.DefaultSemibold>{t('Meta')}</Text.DefaultSemibold>
              <Flex align={'flex-end'} direction={'column'}>
                {isRecommendationLoading ? (
                  <>
                    <Skeleton height={16} width={32} />
                    <Skeleton height={24} width={80} />
                  </>
                ) : (
                  <>
                    <Text.SmallRegular>{numeral(metaPercent / 100).format('0%')}</Text.SmallRegular>
                    <Text.LargeSemibold
                      className={classNames(styles.dollarAmount, {
                        [styles.dollarAmountInvalid]: metaBelowMin,
                      })}
                    >
                      {numeral(shownMetaBudget).format('$0,0.00')}
                    </Text.LargeSemibold>
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>

          {/* When locked, the tooltip explains why the split can't be adjusted;
           * when unlocked the Tooltip renders its child as-is (no title). */}
          <Tooltip position={'top'} title={isSplitLocked ? lockTooltip : undefined}>
            <SplitSlider
              dataName={'channel_split_slider'}
              disabled={areControlsDisabled}
              formatValue={(v): string => numeral(v / 100).format('0%')}
              hideValues={true}
              leftIcon={<span className={styles.displayIcon}>{getIconForAction('displayAd')}</span>}
              /* Drop the label when the region collapses to its minimum so the
               * affordance is just the icon, perfectly centered. */
              leftLabel={displayPercent > MIN_SPLIT ? t('Display') : undefined}
              max={MAX_SPLIT}
              min={MIN_SPLIT}
              minRegionFlex={MIN_SPLIT}
              onChange={handleSliderChange}
              onChangeEnd={handleSliderChangeEnd}
              rightIcon={<span className={styles.metaIcon}>{getIconForAction('meta')}</span>}
              rightLabel={displayPercent < MAX_SPLIT ? t('Meta') : undefined}
              step={1}
              value={displayPercent}
            />
          </Tooltip>

          {renderSplitStatus()}
        </Flex>
      </Card.Content>
    </Card>
  );
}

export default observer(ChannelBudgetSplit);
